import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { LocaleEnum, useLocaleContext } from '@/website/contexts/AppContext';
import { useCustomNavigate } from '@/website/hooks/useCustomNavigate';
import { useUrlQueryParameters } from '@/website/hooks/useUrlQueryParameters';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { Routes } from '@/website/util/routes';
import { Transition } from '@headlessui/react';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { PromotionEntityType, businessInsuranceKey, goodsInTransitOldCompatibilityKey, useLocalTranslation } from '@jove-frontend/uikit';
import { BusinessCenterOutlined, BusinessOutlined, HomeWorkOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { cloneDeep, isArray } from 'lodash';

export type ProductItem = {
  href: string;
  label: string;
  external?: boolean;
  active?: boolean;
  locale?: LocaleEnum[];
  key?: string;
  childrenProductList?: ProductItem[];
  icon?: React.ReactNode;
  isSelected?: boolean;
  originalHref?: string;
};

const getProductItems = ($t): ProductItem[] => [
  {
    label: $t('kit:business_insurance'),
    href: '/personal/business-insurance',
    key: businessInsuranceKey,
    active: true,
    childrenProductList: [
      {
        label: $t('page:ProductsBar.commercial_business'),
        originalHref: '/personal/commercial-combined-business',
        href: '/personal/commercial-combined-business',
        key: 'commercial_business',
        icon: <BusinessOutlined className="h-5 w-5" />,
        isSelected: false,
      },
      {
        label: $t('page:ProductsBar.contractor_sme'),
        originalHref: '/personal/business-insurance',
        href: '/personal/business-insurance',
        key: 'contractor_sme',
        icon: <HomeWorkOutlined className="h-5 w-5" />,
        isSelected: false,
      },
      {
        label: $t('page:ProductsBar.recruitment_payroll_firms'),
        originalHref: '/personal/recruitment',
        href: '/personal/recruitment',
        key: 'recruitment_payroll',
        icon: <BusinessCenterOutlined className="h-5 w-5" />,
        isSelected: false,
      },
    ],
    isSelected: false,
  },
  {
    label: $t('kit:greateAmericanCover.motor_truck_cargo'),
    href: '/personal/motor-truck-cargo',
    active: true,
    locale: [LocaleEnum.US],
    isSelected: false,
    childrenProductList: [],
  },
  {
    label: $t('kit:goods_in_transit'),
    href: '/personal/goods-in-transit',
    active: true,
    key: goodsInTransitOldCompatibilityKey,
    locale: [LocaleEnum.UK, LocaleEnum.EU],
    isSelected: false,
    childrenProductList: [],
  },
];

type Props = {
  isOpen: boolean;
};

export const ProductsBar = ({ isOpen }: Props) => {
  const { push } = useCustomNavigate();
  const { $t } = useLocalTranslation();

  /**
   * methods
   */
  const switchToJoveAction = () => {
    push({ path: Routes.switchToJoveHome(), replace: true });
  };

  return (
    <div className="relative hidden w-full items-center justify-center md:block">
      <Transition
        show={isOpen}
        enter="transition-opacity duration-75"
        enterFrom="opacity-0"
        enterTo="opacity-100 border-t"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        className="absolute left-0 top-0 w-full bg-white shadow-md"
      >
        <ul className="container  flex list-none items-center justify-evenly py-2 text-sm font-semibold">
          <li className="mx-6 h-4"></li>
          <ProductsList />
          <div className="flex flex-col">
            <div className="flex cursor-pointer flex-row items-center space-x-2" onClick={switchToJoveAction}>
              <p>{$t('page:ProductsBar.switch_to_jove')}</p>
              <ArrowRightIcon className="h-4 w-4" />
            </div>
          </div>
        </ul>
      </Transition>
    </div>
  );
};

export const ProductsBarMobile = () => {
  const { push } = useCustomNavigate();
  const { $t } = useLocalTranslation();

  /**
   * methods
   */
  const switchToJoveAction = () => {
    push({ path: Routes.switchToJoveHome(), replace: true });
  };

  return (
    <div className="mt-5">
      <h3 className="text-[22px] text-white">{$t('page:ProductsBar.our_products')}</h3>
      <ul className="mt-3 flex flex-col">
        <ProductsList isMobile={true} />
      </ul>
      <div className="mt-5 flex cursor-pointer flex-col text-white" onClick={switchToJoveAction}>
        <div className="flex cursor-pointer flex-row items-center space-x-2">
          <p className="text-lg font-medium">{$t('page:ProductsBar.switch_to_jove')}</p>
          <ArrowRightIcon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
};

type ProductsListProps = {
  isMobile?: boolean;
};

const ProductsList = ({ isMobile }: ProductsListProps) => {
  const router = useRouter();
  const { locale } = useLocaleContext();
  const { $t } = useLocalTranslation();
  const { getBusinessInsuranceCompleteLink } = useUrlQueryParameters();
  const [productItemsList, setProductItemsList] = useState<ProductItem[]>([]);
  const {
    state: { promotionInfo },
  } = promotionInfoStore();

  /**
   * effects
   */
  useEffect(() => {
    let list = cloneDeep(getProductItems($t)).map((item) => {
      if (router.query['promotionSlug']) {
        if (item.key === businessInsuranceKey) {
          item.href = getBusinessInsuranceCompleteLink();
          item.childrenProductList = item.childrenProductList.map((aItem) => {
            aItem.href = `${aItem.href}/${router.query['promotionSlug']}`;
            return aItem;
          });
        } else if (item.key === goodsInTransitOldCompatibilityKey) {
          item.href = `${item.href}/${router.query['promotionSlug']}`;
        }
      }
      return item;
    });

    // Router
    list.forEach((item) => {
      if (isArray(item.childrenProductList) && item.childrenProductList.length > 0) {
        item.childrenProductList.forEach((aItem) => {
          aItem.isSelected = router.pathname.includes(aItem.originalHref);
        });
      } else {
        item.isSelected = router.pathname.includes(item.href);
      }
    });

    // Promotion
    if (promotionInfo) {
      const { promotion_type, insurance_type } = promotionInfo;
      if ([PromotionEntityType.BROKER_PROMOTION, PromotionEntityType.PARTNER_PROMOTION].includes(promotion_type)) {
        list = list.filter((item) => item.key === insurance_type);
        list.forEach((item) => {
          item.childrenProductList = item.childrenProductList.filter((aItme) =>
            promotionInfo?.['payload']?.landing_page_theme?.includes(aItme.key as any)
          );
        });
      }
    }

    setProductItemsList(list);
  }, [promotionInfo]);

  /**
   * methods
   */
  const redirectPageAction = (item: ProductItem) => {
    router.replace(item.href);
  };

  const itemClassName = clsx('block cursor-not-allowed px-4 py-2 capitalize', {
    'text-gray-400': !isMobile,
    'text-white/30': isMobile,
  });
  const activeItemClassName = clsx('block py-2 capitalize', {
    'text-gray-dark hover:text-brand-primary transition-colors duration-75 px-4': !isMobile,
    'text-base font-semibold text-white': isMobile,
  });

  return (
    <>
      {productItemsList.map((item) => {
        if (item.locale && !item.locale.includes(locale)) return null;

        return (
          <li key={item.label}>
            {item.active ? (
              item.childrenProductList?.length > 0 ? (
                <Box className="mb-5 flex flex-col space-y-2">
                  <Typography variant="subtitle2" className={clsx('font-bold', isMobile ? 'text-white' : 'text-black')}>
                    {item.label}
                  </Typography>
                  <Box className="flex flex-col space-y-2">
                    {item.childrenProductList.map((aItem, index) => {
                      const isSelected = !!aItem.isSelected;

                      return (
                        <Box
                          key={index}
                          className={clsx(
                            'hover:text-brand-secondary flex cursor-pointer flex-row items-center space-x-2',
                            isSelected ? (isMobile ? 'text-[#6AE5BB]' : 'text-brand-secondary') : ''
                          )}
                          onClick={() => redirectPageAction(aItem)}
                        >
                          <Box
                            className={clsx(
                              'flex h-8 w-8 items-center justify-center rounded-full',
                              isSelected ? (isMobile ? 'bg-[#6AE5BB] text-white' : 'bg-brand-secondary text-white') : 'bg-grey-5 text-black'
                            )}
                          >
                            {aItem.icon}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            className={clsx('font-normal', isMobile ? (isSelected ? 'text-[#6AE5BB]' : 'text-white') : 'text-black')}
                          >
                            {aItem.label}
                          </Typography>
                          <ArrowRightIcon
                            className={clsx('inline-block h-4', isMobile ? (isSelected ? 'text-[#6AE5BB]' : 'text-white') : 'text-black')}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Link
                  href={item.href}
                  className={clsx(activeItemClassName, isMobile && item.isSelected ? 'text-[#6AE5BB]' : '')}
                  target={item.external ? '_blank' : '_self'}
                >
                  {item.label}
                  <ArrowRightIcon className={clsx('ml-2 inline-block h-4', isMobile && item.isSelected ? 'text-[#6AE5BB]' : '')} />
                </Link>
              )
            ) : (
              <p className={itemClassName}>
                {item.label}
                <span className="block font-normal">{$t('page:ProductsBar.coming_soon')}</span>
              </p>
            )}
          </li>
        );
      })}
    </>
  );
};
